import type { FindFn } from "@shared/models/analytics/base"
import type { Course, CourseDateAnnotation } from "@shared/models/course"
import { findCourseDatesOff } from "cc-frontend/lib/dates-off-calculator"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import _, { partial } from "lodash"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultCourseDateActivity from "./default-course-date-activity"
import defaultCourseDateContext from "./default-course-date-context"
import defaultCourseDateModels from "./default-course-date-models"

interface Payload {
  course: Course
  annotation: CourseDateAnnotation
  originalDatesOff: Array<string>
  calledFrom: string
  rotationTitle?: string
  datesForcedOn?: Array<string> | null
}

export function prepareCreateAnnotation(payload: Payload): PreparedAction {
  return {
    name: COURSE_DATE_ANNOTATION_CREATE.name,
    payload: payload,
    patches: COURSE_DATE_ANNOTATION_CREATE.patches(payload),
    undoPatches: COURSE_DATE_ANNOTATION_CREATE.undoPatches(payload),
    narrative: partial(COURSE_DATE_ANNOTATION_CREATE.narrative, payload),
  }
}

export const COURSE_DATE_ANNOTATION_CREATE = {
  name: "COURSE_DATE_ANNOTATION_CREATE",

  patches(payload: Payload): Array<PatchModel> {
    // create patch for annotation
    let newAnnotations = payload.course.attributes.calendar.dateAnnotations.concat([
      payload.annotation,
    ])
    let allDatesOff = findCourseDatesOff(newAnnotations)
    let annotationPatch = Patch.create("course", payload.course.id)
      .push("attributes.calendar.dateAnnotations", payload.annotation)
      .set("attributes.calendar.datesOff", allDatesOff)
      .inc("attributes.calendar.version", 1).patch

    // create patch for dates that are forced on, but only use if that array isn't null.
    let newCourseDates = _.map(payload.course.attributes.calendar.dates, (courseDate) => {
      if (
        courseDate.type === "course-date-custom" &&
        _.includes(payload.datesForcedOn, courseDate.attributes.dateString)
      ) {
        let cd = _.cloneDeep(courseDate)
        cd.attributes.isForcedOn = true
        return cd
      } else {
        return courseDate
      }
    })
    let forceOnPatch = Patch.create("course", payload.course.id)
      .set("attributes.calendar.dates", newCourseDates)
      .push("attributes.calendar.datesForcedOn", ["$each", payload.datesForcedOn])
      .inc("attributes.calendar.version", 1).patch

    if (payload.datesForcedOn) {
      return [annotationPatch, forceOnPatch]
    } else {
      return [annotationPatch]
    }
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    //create patch for annotation
    let annotationPatch = Patch.create("course", payload.course.id)
      .pull("attributes.calendar.dateAnnotations", payload.annotation)
      .set(
        "attributes.calendar.datesOff",
        JSON.parse(JSON.stringify(payload.originalDatesOff || []))
      )
      .inc("attributes.calendar.version", 1).patch

    // create patch for dates that are forced on, but only use if that array isn't null.
    let courseDates = JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
    let newCourseDates = _.map(courseDates, (courseDate) => {
      if (
        courseDate.type === "course-date-custom" &&
        _.includes(payload.datesForcedOn, courseDate.attributes.dateString)
      ) {
        let cd = _.cloneDeep(courseDate)
        cd.attributes.isForcedOn = false
        return cd
      } else {
        return courseDate
      }
    })
    let forceOnPatch = Patch.create("course", payload.course.id)
      .set("attributes.calendar.dates", newCourseDates)
      .pull("attributes.calendar.datesForcedOn", [
        JSON.parse(JSON.stringify(payload.datesForcedOn || [])),
      ])
      .inc("attributes.calendar.version", 1).patch

    if (payload.datesForcedOn) {
      return [annotationPatch, forceOnPatch]
    } else {
      return [annotationPatch]
    }
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let models = defaultCourseDateModels(payload, findFn, userId)
    let courseTitle = payload.course.attributes.title
    let numberOfDates

    if (payload.datesForcedOn) {
      numberOfDates = payload.datesForcedOn.length
    } else {
      numberOfDates = ""
    }

    let dateMessage =
      payload.annotation.type === "date-annotation-course-default"
        ? `every ${payload.rotationTitle}`
        : moment(payload.annotation.attributes.dateString).format("ddd, MMM. D, YYYY")
    let forceOnMessage = payload.datesForcedOn
      ? ` But kept ${numberOfDates} lessons that you had already edited turned on.`
      : ""

    let actionTitle =
      // @ts-ignore please fix later when Scott is on parental leave
      payload.annotation.attributes.showAnnotation === false
        ? "Turned Class Off"
        : "Turned Class Off and Added Note"
    // @ts-ignore parental leave
    let addedNote = payload.annotation.attributes.showAnnotation === false ? false : true
    let descriptionStarter =
      // @ts-ignore parental leave
      payload.annotation.attributes.showAnnotation === false
        ? "Turned off"
        : "Turned off and added a note to"
    let context = defaultCourseDateContext(payload, models)
    let newContext = _.assign({}, context, {
      addedNote: addedNote,
      //@ts-ignore parental leave - separate narrative for default and custom
      noteTitle: payload.annotation.attributes.title,
      alsoForcedDatesOn: payload.datesForcedOn ? true : false,
      datesForcedOn: payload.datesForcedOn,
      type: payload.annotation.type,
    })

    return {
      title: actionTitle,
      titleForAnalytics: "Lesson - Toggle Off",
      description: `${descriptionStarter} your ${courseTitle} class on ${dateMessage}.${forceOnMessage}`,
      context: newContext,
      activity: defaultCourseDateActivity(payload, models, "added"),
    }
  },
}

export default COURSE_DATE_ANNOTATION_CREATE
